import React from 'react'
import { Box, Flex, Text } from '@chilipiper/design-system'
import { Avatar } from '@chilipiper/design-system/src/new'
import { usePersonalDetails } from '../../api-queries/personal-details'
import { useMessageFeedContext } from '../../chat-widget/MessageFeedContext'
import { LiveMessageBlock } from '../../chat-data/types'
import { getName } from '../../helpers/getName'
import { StyledAvatarImage } from '../styles'
import { BlockOrCompositeBlock } from '../compositeBlocks'
import { BlockRender } from './BlockRender'

interface Props {
  block: BlockOrCompositeBlock
  onLiveMessageVisible: (event: LiveMessageBlock) => void
  isLastBlock: boolean
}

export const FeedBlock = ({ block, onLiveMessageVisible, isLastBlock }: Props) => {
  const { guestId } = useMessageFeedContext()

  const isOwnMessage = getIsOwnMessage(block, guestId)

  const displayAvatarImage = !blockTypesWithoutAvatar.includes(block.type) && !isOwnMessage

  const stickToBottom =
    isLastBlock &&
    (block.type === 'ReplyButtons' || block.type === 'EnterPiperAiChat') &&
    block.selected === undefined

  return (
    <Flex
      mt={stickToBottom ? 'auto' : undefined}
      flexDirection='column'
      gap='2xs'
      w='100%'
      alignItems={isOwnMessage ? 'flex-end' : 'flex-start'}
      title={
        block.type === 'LiveMessage' || block.type === 'Message' || block.type === 'PiperAiMessage'
          ? dateTimeFormatter.format(new Date(block.timestamp))
          : undefined
      }
    >
      <BlockRender
        isLastBlock={isLastBlock}
        block={block}
        isOwnMessage={isOwnMessage}
        onLiveMessageVisible={onLiveMessageVisible}
      />
      {displayAvatarImage && <BlockAvatar block={block} />}
    </Flex>
  )
}

const BlockLayout = ({ name, image }: { name: string; image: React.ReactNode }) => {
  return (
    <Flex gap='2xs'>
      {image}
      <Text textStyle='detail-m' color='fg/moderate'>
        {name}
      </Text>
    </Flex>
  )
}

const BlockAvatar = ({ block }: { block: BlockOrCompositeBlock }) => {
  const { orgDetails, aiDetails, tenantId, guestId } = useMessageFeedContext()
  const isChiliPiperUser =
    block.author !== 'bot' &&
    block.author !== `${guestId}_guest` &&
    block.author !== `${guestId}_bot`
  const { data: assignee } = usePersonalDetails(tenantId, isChiliPiperUser ? block.author : '')
  const isPiperAi = block.type === 'PiperAiMessage'

  if (isChiliPiperUser) {
    return (
      <BlockLayout
        name={getName(assignee) ?? ''}
        image={
          <Avatar
            id={block.author}
            name={getName(assignee) ?? ''}
            image={assignee?.imageUrl}
            size={4}
          />
        }
      />
    )
  }

  if (isPiperAi) {
    return (
      <BlockLayout
        name={aiDetails?.name ?? ''}
        image={
          typeof aiDetails?.logo === 'string' ? (
            <StyledAvatarImage size='16px' borderRadius='4px' src={aiDetails?.logo} alt='' />
          ) : (
            <Box w='16px' h='16px'>
              {aiDetails?.logo}
            </Box>
          )
        }
      />
    )
  }

  return (
    <BlockLayout
      name={orgDetails?.name ?? ''}
      image={<StyledAvatarImage size='16px' borderRadius='4px' src={orgDetails?.logo} alt='' />}
    />
  )
}

const blockTypesWithoutAvatar = ['ReplyButtons', 'SystemMessage'] as Array<
  BlockOrCompositeBlock['type']
>

const getIsOwnMessage = (block: BlockOrCompositeBlock, guestId?: string) => {
  if (block.type === 'Message' || block.type === 'LiveMessage') {
    return block.author === `${guestId}_guest`
  }
  if (block.author === 'bot') {
    return false
  }
  // regular blocks should be considered own message and displayed without avatar
  return true
}

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})
